import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, TextField, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import api from '../axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';

const LIME = '#00F1A7';
const DARK = '#1C1C1E';

function Turmas() {
  const [openModal, setOpenModal] = useState(false);
  const [periodos, setPeriodos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [nome, setNome] = useState('');
  const [periodoId, setPeriodoId] = useState('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [editId, setEditId] = useState(null);
  const [editNome, setEditNome] = useState('');
  const [editPeriodoId, setEditPeriodoId] = useState('');

  const fetchTurmas = async () => {
    try {
      const res = await api.get('/turmas/');
      setTurmas(res.data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar turmas');
    }
  };

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      setErro('');
      try {
        const [resTurmas, resPeriodos] = await Promise.all([
          api.get('/turmas/'),
          api.get('/periodos/')
        ]);
        setTurmas(resTurmas.data);
        setPeriodos(resPeriodos.data);
      } catch (err) {
        setErro(errorMsg(err) || 'Erro ao carregar dados');
      }
      setLoading(false);
    };
    carregar();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setErro(''); setSucesso('');
    if (!nome || !periodoId) {
      setErro('Preencha nome e período');
      return;
    }
    try {
      await api.post(`/turmas/?nome=${encodeURIComponent(nome)}&periodo_id=${periodoId}`);
      setNome(''); setPeriodoId('');
      setSucesso('Turma cadastrada!');
      setOpenModal(false);
      fetchTurmas();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro de conexão');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/turmas/${id}`);
      fetchTurmas();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir turma');
    }
  };

  const handleEdit = (id) => {
    const turma = turmas.find(t => t.id === id);
    setEditId(id);
    setEditNome(turma.nome);
    setEditPeriodoId(turma.periodo_id);
    setErro('');
    setSucesso('');
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setEditNome('');
    setEditPeriodoId('');
  };

  const handleSaveEdit = async (id) => {
    setErro(''); setSucesso('');
    if (!editNome || !editPeriodoId) {
      setErro('Preencha nome e período');
      return;
    }
    try {
      await api.put(`/turmas/${id}`, { nome: editNome, periodo_id: editPeriodoId });
      setSucesso('Turma atualizada!');
      setEditId(null);
      fetchTurmas();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro de conexão');
    }
  };

  const getNomePeriodo = (periodoId) => {
    const p = periodos.find(p => p.id === periodoId);
    return p ? `${p.quinzena} - ${p.mes}/${p.ano}` : '';
  };

  if (loading) return <><Header /><ContextNavBar context="startfy" /><Container sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container><Footer /></>;
  if (erro && turmas.length === 0) return <><Header /><ContextNavBar context="startfy" /><Container sx={{ py: 4 }}><Alert severity="error">{erro}</Alert></Container><Footer /></>;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Button component={RouterLink} to="/area-startfy" variant="outlined" startIcon={<ArrowBackIcon />} sx={{ mb: 3 }}>
        Voltar
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DARK, fontSize: { xs: '1.3rem', md: '2rem' } }}>
          Turmas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
          sx={{ bgcolor: LIME, color: DARK, fontWeight: 600, '&:hover': { filter: 'brightness(0.9)' } }}
        >
          Novo
        </Button>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
      {sucesso && <Alert severity="success" sx={{ mb: 2 }}>{sucesso}</Alert>}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Período</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 120, whiteSpace: 'nowrap' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {turmas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ color: '#999', py: 4 }}>
                  Nenhuma turma cadastrada
                </TableCell>
              </TableRow>
            ) : (
              turmas.map((turma) => (
                <TableRow key={turma.id} hover>
                  {editId === turma.id ? (
                    <>
                      <TableCell>
                        <TextField
                          value={editNome}
                          onChange={e => setEditNome(e.target.value)}
                          size="small"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={editPeriodoId}
                          onChange={e => setEditPeriodoId(e.target.value)}
                          size="small"
                          fullWidth
                        >
                          {periodos.map(periodo => (
                            <MenuItem key={periodo.id} value={periodo.id}>
                              {`${periodo.quinzena} - ${periodo.mes}/${periodo.ano}`}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Salvar">
                            <IconButton color="primary" size="small" onClick={() => handleSaveEdit(turma.id)}>
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton color="inherit" size="small" onClick={handleCancelEdit}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 600 }}>{turma.nome}</TableCell>
                      <TableCell>{getNomePeriodo(turma.periodo_id)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" color="info" onClick={() => handleEdit(turma.id)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDelete(turma.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: DARK }}>Cadastrar Turma</DialogTitle>
        <Box component="form" onSubmit={handleAdd}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Nome" value={nome} onChange={e => setNome(e.target.value)} fullWidth size="small" />
            <TextField
              select
              label="Período"
              value={periodoId}
              onChange={e => setPeriodoId(e.target.value)}
              fullWidth
              size="small"
            >
              {periodos.map(periodo => (
                <MenuItem key={periodo.id} value={periodo.id}>
                  {`${periodo.quinzena} - ${periodo.mes}/${periodo.ano}`}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: LIME, color: DARK, fontWeight: 600, '&:hover': { filter: 'brightness(0.9)' } }}
            >
              Cadastrar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
}

export default function TurmasPageWrapper(props) {
  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };
  return (
    <>
      <Header onLogout={handleLogout} />
      <ContextNavBar context="startfy" />
      <Turmas {...props} />
      <Footer />
    </>
  );
}
